import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {
  SINGLE_CHART_OUTPUT_STEP_DATA_NAME,
  SINGLE_DATASET_SAMPLED_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {
  ANALYSIS_TOOL_SPACING,
  SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES,
} from 'analysis-tool/AnalysisToolConstants';
import {AnalysisGraphQL, GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {
  findAndAssertStepData,
  findSingleStepOutputDataFrameId,
  findNullableStepData,
} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import useChartViewPreview from 'analysis-tool/ui/charting/useChartViewPreview';
import {GetChartViewDataFrame_dataFrame_DataFrameNode as VisualizationStepEditorDataFrameNode} from 'analysis-tool/ui/main-view/__generated/GetChartViewDataFrame';
import QueryingChartAndDataFrame from 'analysis-tool/ui/main-view/QueryingChartAndDataFrame';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {Chart} from 'charts/ChartTypes';
import {DataFrame, DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import DataFrameWarningBanner from 'data-frames/graphql/DataFrameWarningBanner';
import isSystemColumn from 'data-frames/util/isSystemColumn';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {assertIsAccessible, isInaccessible} from 'graphql-client/InaccessibleInfo';
import getVisualizationType from 'insights/analytics-visualizations/data/getVisualizationType';
import {PANE_MARGIN} from 'insights/InsightsConstants';
import {
  VisualizationConfig,
  VisualizationType,
} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import staticURL from 'lib/staticURL';
import {FONT_PROXIMA_18, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import FormInput from 'ui-library/bs/FormInput';
import {RegressionAnalysis} from 'ui-library/charting/statistics/regression/RegressionTypes';
import {SuccessfulAnalysis} from 'ui-library/charting/statistics/Types';
import * as Colors from 'ui-library/Colors';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import LoadingOverlay from 'ui-library/LoadingOverlay';
import Pane from 'ui-library/pane/Pane';
import {SaveState, SaveStatusIndicator} from 'ui-library/SaveStatusIndicator';
import UIPlaceholder from 'ui-library/UIPlaceholder';

import VisualizationBlock from '../VisualizationBlock';
import EditorSidebar, {EditorSidebarProps} from './EditorSidebar';

const expandPreviewGlobalState = makeUseGlobalClientState<boolean>(false);

export interface VisualizationEditorSkeletonProps {
  analysis: AnalysisGraphQL;
  clientStateId: string;
  inputDataFrameId: string;
  onChangeVisualizationConfig: (visualizationConfig: VisualizationConfig) => void;
  onChangeVisualizationName: (name: string) => void;
  regressionAnalyses?: Array<SuccessfulAnalysis<RegressionAnalysis>>;
  renderFromChart?: boolean;
  renderEditorSidebarChildren: (
    inputDataFrame: VisualizationStepEditorDataFrameNode,
    invalidatePreview: () => void
  ) => JSX.Element;
  renderPreviewDataFrames: (
    inputDataFrame: VisualizationStepEditorDataFrameNode,
    isExpandedPreviewState: boolean,
    setIsExpandedPreviewState: (newState: boolean) => void
  ) => JSX.Element;
  sidebarProps: Pick<
    EditorSidebarProps,
    | 'disabledVisualizationTypes'
    | 'doneButtonText'
    | 'doneButtonIsDisabled'
    | 'enableAnalysisTab'
    | 'enableAggregation'
    | 'enableFormatTab'
    | 'enableFormatTabForLineChart'
    | 'enableErrorBarColumn'
    | 'onClickDoneButton'
    | 'typeSelectorIsDisabled'
    | 'visualizationEditorIsDisabled'
  >;
  viewSteps: Array<GetAnalysisPipelineStep>;
  canAddNewSteps: boolean;
  visualizationConfig: VisualizationConfig | null;
  visualizationName: string;
  visualizationSaveState: SaveState.SAVING | SaveState.SAVED | SaveState.FAILED;
  warnings?: Array<string>;
}

export default function VisualizationEditorSkeleton(props: VisualizationEditorSkeletonProps): JSX.Element {
  const {inputDataFrameId, viewSteps} = props;
  const lastViewStep = viewSteps[viewSteps.length - 1];

  function hasChartOutput(): boolean {
    if (lastViewStep.definition.stepType === PipelineStepType.REGRESSION) {
      const chartOutputStepData = findNullableStepData(
        lastViewStep,
        SINGLE_CHART_OUTPUT_STEP_DATA_NAME,
        false
      );
      return chartOutputStepData != null ? chartOutputStepData.charts.length > 0 : false;
    }

    return (
      lastViewStep.definition.stepType === PipelineStepType.CHART_VIEW &&
      lastViewStep.statusCode === PipelineStepStatusCode.SUCCEEDED
    );
  }

  function getChartIdOrUndefined(): string | undefined {
    if (!hasChartOutput()) {
      return;
    }

    return assertIsAccessible(
      findAndAssertStepData(lastViewStep, SINGLE_CHART_OUTPUT_STEP_DATA_NAME, false).charts[0]
    ).id;
  }

  function getSampledDataFrameFromOutputOrNull(): string | null {
    if (findNullableStepData(lastViewStep, SINGLE_DATASET_SAMPLED_OUTPUT_STEP_DATA_NAME, false) == null) {
      return null;
    }

    const outDf = findSingleStepOutputDataFrameId(lastViewStep, SINGLE_DATASET_SAMPLED_OUTPUT_STEP_DATA_NAME);

    return outDf;
  }

  return (
    <QueryingChartAndDataFrame
      renderLoading={() => (
        <div className={css(visualizationEditorSkeletonStyles.previewAreaPane)}>
          <CenteredHorizontalThrobber />
        </div>
      )}
      chartId={getChartIdOrUndefined()}
      inputDataFrameId={getSampledDataFrameFromOutputOrNull() ?? inputDataFrameId}
      fullDataFrameId={inputDataFrameId}
    >
      {({dataFrame: dataFrameNode, numRows, chart}) => {
        assert(!isInaccessible(dataFrameNode), 'User does not have access to input data frame');

        return (
          <VisualizationEditorSkeletonInner
            {...props}
            dataFrameNode={dataFrameNode}
            chart={chart}
            numRows={numRows}
          />
        );
      }}
    </QueryingChartAndDataFrame>
  );
}

type VisualizationEditorSkeletonInnerProps = Pick<
  VisualizationEditorSkeletonProps,
  | 'analysis'
  | 'clientStateId'
  | 'onChangeVisualizationConfig'
  | 'onChangeVisualizationName'
  | 'renderEditorSidebarChildren'
  | 'renderFromChart'
  | 'renderPreviewDataFrames'
  | 'regressionAnalyses'
  | 'sidebarProps'
  | 'viewSteps'
  | 'canAddNewSteps'
  | 'visualizationConfig'
  | 'visualizationName'
  | 'visualizationSaveState'
  | 'warnings'
> & {
  dataFrameNode: VisualizationStepEditorDataFrameNode;
  chart: Chart | null;
  numRows: number;
};

function VisualizationEditorSkeletonInner({
  analysis,
  dataFrameNode,
  chart,
  clientStateId,
  numRows,
  onChangeVisualizationConfig,
  onChangeVisualizationName,
  regressionAnalyses,
  renderEditorSidebarChildren,
  renderFromChart,
  renderPreviewDataFrames,
  sidebarProps,
  viewSteps,
  canAddNewSteps,
  visualizationConfig,
  visualizationName,
  visualizationSaveState,
  warnings,
}: VisualizationEditorSkeletonInnerProps): JSX.Element {
  const [isExpandedPreviewState, setIsExpandedPreviewState] =
    expandPreviewGlobalState.useGlobalClientState(clientStateId);
  const visualizationType = visualizationConfig == null ? null : getVisualizationType(visualizationConfig);
  const dataFrame: DataFrame =
    visualizationType === VisualizationType.TABLE
      ? dataFrameNode.dataFrameJson
      : dataFrameNode.randomSubsetDataFrameJson;

  const isRunning =
    viewSteps.every((step) => step.statusCode !== PipelineStepStatusCode.FAILED) &&
    (viewSteps
      .slice(0, viewSteps.length - 1)
      .some((step) =>
        [
          PipelineStepStatusCode.RUNNING,
          PipelineStepStatusCode.WAITING,
          PipelineStepStatusCode.BLOCKED,
        ].includes(step.statusCode)
      ) ||
      [PipelineStepStatusCode.RUNNING, PipelineStepStatusCode.BLOCKED].includes(
        viewSteps[viewSteps.length - 1].statusCode
      ));

  const chartPreviewOutput = useChartViewPreview(
    chart,
    dataFrameNode.id,
    clientStateId,
    visualizationConfig,
    !renderFromChart || isRunning
  );

  const lastViewStep = viewSteps[viewSteps.length - 1];
  const showSavingIndicator = ![PipelineStepType.CHART_VIEW, PipelineStepType.TABLE_VIEW].includes(
    lastViewStep.definition.stepType
  );

  const paneTitle = analysis.permissions.writable ? (
    <FormInput
      className="u-flex-grow"
      onChange={(e) => {
        onChangeVisualizationName(e.target.value);
      }}
      selectAllOnClick={false}
      focusOnMount={false}
      value={visualizationName}
      rows={3}
    />
  ) : (
    visualizationName
  );

  function getColumns(dataFrame: DataFrame): Array<DataFrameColumnDef> {
    return dataFrame.columns.filter((column) => !isSystemColumn(column));
  }

  return (
    <LoadingOverlay isFaded={true} isLoading={isRunning}>
      <div
        className={css(visualizationEditorSkeletonStyles.view)}
        {...getTestProps({component: 'ChartEditorView', element: 'root'})}
      >
        <div className={css(visualizationEditorSkeletonStyles.previewAreaPane)}>
          {visualizationConfig == null ? (
            <div className={css(visualizationEditorSkeletonStyles.wrapper)}>
              <Pane title={paneTitle}>
                <UIPlaceholder
                  infoSection={
                    <>
                      <img
                        className={css(visualizationEditorSkeletonStyles.emptyChartImg)}
                        src={staticURL('/static/img/insights/grouped_bar_chart_v2.svg')}
                      />
                      <div className={css(visualizationEditorSkeletonStyles.emptyChartTitle)}>
                        This view is empty
                      </div>
                    </>
                  }
                  actionContent={
                    <span className={css(visualizationEditorSkeletonStyles.emptyChartHint)}>
                      Begin by selecting a view type
                    </span>
                  }
                />
              </Pane>
            </div>
          ) : (
            <>
              {!isExpandedPreviewState && (
                <LoadingOverlay isFaded={true} isLoading={chartPreviewOutput.isLoading}>
                  <div className={css(visualizationEditorSkeletonStyles.wrapper)}>
                    <VisualizationBlock
                      allowRowSelection={BenchlingConfig.ENABLE_ANALYSIS_TOOL_FLAG_ROWS && canAddNewSteps}
                      analysis={analysis}
                      banner={
                        <DataFrameWarningBanner
                          allColumnsJson={dataFrameNode.allColumnsJson}
                          dataFrame={dataFrame}
                          isRandomSubset={visualizationType !== VisualizationType.TABLE}
                          name={visualizationName}
                          totalRows={numRows}
                        />
                      }
                      clientStateId={clientStateId}
                      renderFromChart={visualizationType !== VisualizationType.TABLE && renderFromChart}
                      chart={chartPreviewOutput.chart}
                      chartError={chartPreviewOutput.chartError}
                      dataFrame={dataFrame}
                      dataFrameId={dataFrameNode.id}
                      regressionAnalyses={regressionAnalyses}
                      saveStatus={
                        showSavingIndicator ? (
                          <SaveStatusIndicator
                            dataTestAttrs={{component: 'SaveStatusIndicator', element: 'root'}}
                            key="save-status"
                            saveStatus={visualizationSaveState}
                          />
                        ) : undefined
                      }
                      title={paneTitle}
                      viewSteps={viewSteps}
                      visualizationConfig={visualizationConfig}
                      warnings={warnings}
                    />
                  </div>
                </LoadingOverlay>
              )}
              {visualizationType !== VisualizationType.TABLE && (
                <div
                  className={classNames(
                    css(visualizationEditorSkeletonStyles.previewTableWrapper),
                    !isExpandedPreviewState && css(visualizationEditorSkeletonStyles.unexpandedPreviewTable)
                  )}
                >
                  {renderPreviewDataFrames(dataFrameNode, isExpandedPreviewState, setIsExpandedPreviewState)}
                </div>
              )}
            </>
          )}
        </div>
        <EditorSidebar
          {...sidebarProps}
          onClickDoneButton={async () => {
            await sidebarProps.onClickDoneButton();
            setIsExpandedPreviewState(false);
          }}
          visualizationConfig={visualizationConfig}
          onChangeVisualizationConfig={onChangeVisualizationConfig}
          dataFrameColumnDefs={getColumns(dataFrame)}
        >
          {renderEditorSidebarChildren(dataFrameNode, chartPreviewOutput.invalidatePreview)}
        </EditorSidebar>
      </div>
    </LoadingOverlay>
  );
}

export const visualizationEditorSkeletonStyles = StyleSheet.create({
  allTableChangesSavedWrapper: {
    marginTop: ANALYSIS_TOOL_SPACING,
    marginLeft: ANALYSIS_TOOL_SPACING,
    marginRight: ANALYSIS_TOOL_SPACING,
  },
  divider: {
    marginTop: 10,
    marginBottom: 10,
  },
  emptyChartHint: {
    ...FONT_PROXIMA_18,
    color: Colors.GRAY_DARKER,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    textAlign: 'center',
  },
  emptyChartImg: {
    height: 100,
    width: 100,
  },
  emptyChartTitle: {
    fontSize: 22,
    color: Colors.NEAR_BLACK,
  },
  fullWidth: {
    width: '100%',
  },
  previewAreaPane: {
    background: Colors.GRAY_LIGHTER,
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflowX: 'hidden',
  },
  previewTableWrapper: {
    ...SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES,
    boxShadow: 'none',
  },
  typeSelector: {
    margin: PANE_MARGIN,
  },
  unexpandedPreviewTable: {
    maxHeight: '30%',
  },
  view: {
    backgroundColor: Colors.WHITE,
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    width: '100%',
  },
  wrapper: {
    ...SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES,
    marginBottom: 0,
    overflowY: 'auto',
    height: '100%',
  },
});
